import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Border_Color = () => {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🎨 CSS Border Color</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <code>border-color</code> property sets the color of an element’s
        border. It is used along with <code>border-style</code> and{" "}
        <code>border-width</code> to display visible borders.
      </p>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock code={`selector { border-color: color; }`} />
        <p>
          You can define the border color using:
          <br />• Named colors (like <code>red</code>, <code>blue</code>) <br />
          • HEX values (<code>#ff0000</code>) <br />• RGB values (
          <code>rgb(255, 0, 0)</code>) <br />• HSL values (
          <code>hsl(0, 100%, 50%)</code>)
        </p>
      </div>

      {/* Basic Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">Basic Examples</h2>
        <CodeCopyBlock
          code={`p { border: 3px solid red; }
p { border: 3px solid #00ff00; }
p { border: 3px solid rgb(0,0,255); }
p { border: 3px solid hsl(200, 100%, 50%); }`}
        />

        <div
          style={{
            display: "flex",
            gap: "1rem",
            flexWrap: "wrap",
            marginTop: "1rem",
          }}
        >
          <div style={{ border: "3px solid red", padding: "10px" }}>Red</div>
          <div style={{ border: "3px solid #00ff00", padding: "10px" }}>
            #00ff00
          </div>
          <div style={{ border: "3px solid rgb(0,0,255)", padding: "10px" }}>
            RGB Blue
          </div>
          <div
            style={{ border: "3px solid hsl(200, 100%, 50%)", padding: "10px" }}
          >
            HSL Blue
          </div>
        </div>
      </div>

      {/* Individual Sides */}
      <div className="tutorial-section">
        <h2 className="section-title">Individual Sides</h2>
        <p>
          You can set colors for each side separately using{" "}
          <code>border-top-color</code>, <code>border-right-color</code>,{" "}
          <code>border-bottom-color</code>, and <code>border-left-color</code>.
        </p>
        <CodeCopyBlock
          code={`div {
  border-style: solid;
  border-width: 5px;
  border-top-color: red;
  border-right-color: green;
  border-bottom-color: blue;
  border-left-color: orange;
}`}
        />
        <div
          style={{
            borderStyle: "solid",
            borderWidth: "5px",
            borderTopColor: "red",
            borderRightColor: "green",
            borderBottomColor: "blue",
            borderLeftColor: "orange",
            padding: "15px",
            marginTop: "0.5rem",
          }}
        >
          🔴 Different colors on each side
        </div>
      </div>

      {/* Shorthand */}
      <div className="tutorial-section">
        <h2 className="section-title">Shorthand Border Color</h2>
        <p>
          Like <code>margin</code> and <code>padding</code>,{" "}
          <code>border-color</code> can accept 1–4 values:
        </p>
        <CodeCopyBlock
          code={`/* 1 value - all sides */
div { border-color: red; }

/* 2 values - top/bottom | left/right */
div { border-color: red blue; }

/* 3 values - top | left/right | bottom */
div { border-color: red blue green; }

/* 4 values - top | right | bottom | left */
div { border-color: red blue green orange; }`}
        />

        <div
          style={{
            borderStyle: "solid",
            borderWidth: "5px",
            borderColor: "red blue green orange",
            padding: "15px",
            marginTop: "0.5rem",
          }}
        >
          🟥🟦🟩🟧 Shorthand colors (top → right → bottom → left)
        </div>
      </div>

      {/* Transparent Borders */}
      <div className="tutorial-section">
        <h2 className="section-title">Transparent Borders</h2>
        <p>
          You can make borders invisible while still taking up space using{" "}
          <code>transparent</code>.
        </p>
        <CodeCopyBlock
          code={`div {
  border: 5px solid transparent;
}`}
        />
        <div
          style={{
            border: "5px solid transparent",
            padding: "15px",
            backgroundColor: "#f0f0f0",
            marginTop: "0.5rem",
          }}
        >
          🚫 Invisible border (but space remains)
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          Now that you know how to use <code>border-color</code>, continue to{" "}
          <a href="/css/border-style" className="learn-more">
            CSS Border Style
          </a>{" "}
          for different border line designs.
        </p>
      </div>
    </div>
  );
};

export default Border_Color;
